import { Component } from '@theme/component';

/**
 * Horizontally scrollable record strip.
 *
 * Native overflow scrolling does the work; the arrows are a convenience for
 * pointer users, which is why they are disabled at the ends rather than
 * wrapping around — a wrap would hide that you have seen everything.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} track
 * @property {HTMLButtonElement} [prev]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
class RecordStrip extends Component {
  requiredRefs = ['track'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.prev?.addEventListener('click', this.#scrollPrev);
    this.refs.next?.addEventListener('click', this.#scrollNext);
    this.refs.track.addEventListener('scroll', this.#syncArrows, { passive: true });
    // Chrome routes vertical wheel input into a horizontal snap container, and
    // overscroll-behavior keeps it there once the strip hits its end — so a
    // reader whose cursor happened to rest on the cards could no longer scroll
    // the page at all. Vertical intent goes back to the page; horizontal
    // wheels and trackpad swipes still reach the strip untouched.
    this.refs.track.addEventListener('wheel', this.#redirectVerticalWheel, { passive: false });

    this.#syncArrows();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.prev?.removeEventListener('click', this.#scrollPrev);
    this.refs.next?.removeEventListener('click', this.#scrollNext);
    this.refs.track.removeEventListener('scroll', this.#syncArrows);
    this.refs.track.removeEventListener('wheel', this.#redirectVerticalWheel);
  }

  /** One card plus its gap, so an arrow press lands on a card edge. */
  get #step() {
    const card = this.refs.track.firstElementChild;
    if (!card) return this.refs.track.clientWidth;

    const gap = parseFloat(getComputedStyle(this.refs.track).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  #redirectVerticalWheel = (event) => {
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
    const page = this.closest('.page-wrapper') || document.scrollingElement;
    page.scrollBy({ top: event.deltaY });
    event.preventDefault();
  };

  #scrollPrev = () => this.refs.track.scrollBy({ left: -this.#step, behavior: 'smooth' });

  #scrollNext = () => this.refs.track.scrollBy({ left: this.#step, behavior: 'smooth' });

  #syncArrows = () => {
    const { scrollLeft, scrollWidth, clientWidth } = this.refs.track;
    // Sub-pixel rounding means scrollLeft rarely hits scrollWidth - clientWidth
    // exactly, so the end test needs slack or "next" never disables.
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft >= scrollWidth - clientWidth - 1;

    if (this.refs.prev) this.refs.prev.disabled = atStart;
    if (this.refs.next) this.refs.next.disabled = atEnd;
  };
}

if (!customElements.get('tt-strip')) {
  customElements.define('tt-strip', RecordStrip);
}
